import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { showErrorSwal } from '@asamco/utils';
import { Subject } from '@microsoft/signalr';
import { DxCheckBoxModule, DxDataGridModule, DxSelectBoxModule, DxTextBoxModule } from 'devextreme-angular';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxiColumnModule, DxoPagingModule, DxoSearchPanelModule, DxoSortingModule } from 'devextreme-angular/ui/nested';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Observable, catchError, firstValueFrom, map, of, switchMap, throwError } from 'rxjs';
import { CompanyType } from '../../../enums/company-type.enum';
import { CompanyDto, UserCompanyView, UserDto } from '../../../services/al-company-v2.service';
import { AlUserCompanyV2Service, UserCompanyDto } from '../../../services/al-user-company-v2.service';
import { AdminWarningBannerComponent } from '../admin-warning-banner.component';
import { EditorPreparedEvent, RowRemovingEvent } from 'devextreme/ui/data_grid';
import { rowRemovingConfirmation } from '@asamco/application-link/shared';
import { uniqBy } from 'lodash-es';
import { NameIdPair } from '../../../interfaces/name-id-pair.interface';

type ViewModel = {
	state: 'loading'
} | {
	state: 'ready'
	dataSource: DataSource;
	companyDatasource: NameIdPair[];
	userDatasource: NameIdPair[];
} | {
	state: 'error'
}

@Component({
	selector: 'app-shell-admin-user-company-management-grid',
	templateUrl: './admin-user-company-management-grid.component.html',
	styleUrls: ['./admin-user-company-management-grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		FormsModule,
		NgIf,
		ReactiveFormsModule,
		DxTextBoxModule,
		DxCheckBoxModule,
		DxSelectBoxModule,
		DxDataGridModule,
		DxoSortingModule,
		DxoPagingModule,
		DxoSearchPanelModule,
		DxiColumnModule,
		DxTemplateModule,
		AsyncPipe,
		AdminWarningBannerComponent,
	],
})

export class AdminUserCompanyManagementGridComponent implements OnDestroy {
	private readonly destroy$ = new Subject<void>();

	public readonly viewModel$: Observable<ViewModel> = of({
		state: 'loading'
	}).pipe(
		switchMap(() => this.userCompanyService.getView()),
		map((ucView) => {
			const viewModel: ViewModel = {
				state: 'ready',
				dataSource: this.buildDataSource(),
				userDatasource: uniqBy(ucView, ucv => ucv.userId).map(ucv => ({id: ucv.userId, name: ucv.userName})),
				companyDatasource: uniqBy(ucView, ucv => ucv.companyId).map(ucv => ({id: ucv.companyId, name: ucv.companyName})),
			};
			return viewModel;
		}),
		catchError(error => {
			showErrorSwal(error);
			const viewModel: ViewModel = {
				state: 'error'
			};
			return of(viewModel);
		}),
	);

	constructor(
		private userCompanyService: AlUserCompanyV2Service,
	) {

	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public onEditorPreparing(event: EditorPreparedEvent<UserCompanyDto>) {
		if (event.parentType !== "dataRow") {
			return;
		}

		if (event.row.isNewRow) {
			return;
		}

		if (['isOwner'].findIndex(field => field === event.dataField) !== -1) {
			return;
		}

		const editorOptions = (<any>event).editorOptions;
		editorOptions.disabled = true;
	}

	public getCompanyEnumValue(companyTypeValue: number) {
		return CompanyType[companyTypeValue];
	}

	private buildDataSource(): DataSource {
		return new DataSource<UserCompanyView, UserCompanyView>({
			key: 'id',
			store: new CustomStore({
				// key: 'id',
				load: _loadOptions => {
					return firstValueFrom(this.userCompanyService.getView().pipe(this.handleDataStoreError()));
				},
				byKey: (key: UserCompanyView) => {
					return firstValueFrom(this.userCompanyService.getViewElement(key.id).pipe(this.handleDataStoreError()));
				},
				update: (key: UserCompanyView, values: UserCompanyView) => {
					// TODO: DevExtreme expects to only pass changed values. I don't want to keep track of that data
					// so that we can do a PUT. So just don't define a key, and pass the whole object as 'key'.
					const newValues =  Object.assign(key, values);
					// if (key !== values.id) {
					// 	throw new Error('ID is not the same when updating company.');
					// }
					return firstValueFrom(this.userCompanyService.put(<any>newValues).pipe(this.handleDataStoreError()));
				},
				insert: values => {
					return firstValueFrom(this.userCompanyService.post(<any>values).pipe(switchMap(r => this.userCompanyService.getViewElement(r.id)), this.handleDataStoreError()));
				},
				remove: async (key: UserCompanyView) => {
					await firstValueFrom(this.userCompanyService.delete(key.id).pipe(this.handleDataStoreError(), map(() => undefined)));
				},
			}),
		});
	}

	public rowRemoving($event: RowRemovingEvent<UserCompanyView>, viewModel: ViewModel) {
		if (viewModel.state !== 'ready') {
			// Unexpected.
			console.log(`Unexpected state. Expected 'ready', got '${viewModel.state}'.`);
			return;
		}

		$event.cancel = rowRemovingConfirmation({
			text: `Remove user '${$event.data.userName}' from company '${$event.data.companyName}'?`
		});
	}

	private handleDataStoreError<T>(): (source: Observable<T>) => Observable<T> {
		return (source: Observable<T>): Observable<T> =>
			source.pipe(
				catchError(error => {
					showErrorSwal(error)
					// Let Dx do its thing as well.
					return throwError(() => error);
				})
			);
	}
}
